import type React from 'react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Box, Card, Heading, ShowHide } from '@zillow/constellation-10';
import { css } from '@/styled-system/css';

import MarketPreview from './MarketPreview';
import FilterTray from 'app/shared/components/FilterTray';
import { useFilter, useMarketTrendsFilters } from 'app/shared/hooks/useFilters';
import { useAppDispatch } from 'app/shared/flux/hooks';

import type { Market } from 'app/shared/types/Market';

interface MarketPreviewsProps {
  markets: Array<Market>;
  heading: ReactNode;
  subject: string;
  useFilters?: boolean;
  emitAnalyticsEvent?: () => void;
}

const MarketPreviews: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<MarketPreviewsProps>>> = (
  props,
) => {
  const { heading, subject, useFilters = false, emitAnalyticsEvent } = props;
  const dispatch = useAppDispatch();
  const filter = useFilter();
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { bedrooms, setBedrooms, propertyTypes, setPropertyTypes } = useMarketTrendsFilters(filter, dispatch);

  const markets = props.markets.filter((market) => market.medianRent !== 0); // dont include when theres no data
  const filterDisplay = (
    <FilterTray
      bedrooms={bedrooms}
      setBedrooms={setBedrooms}
      propertyTypes={propertyTypes}
      setPropertyTypes={setPropertyTypes}
      loading={loading}
      setLoading={setLoading}
      // oxlint-disable-next-line constellation/no-raw-spacing-values -- keeps the exact 60px the inline style used
      className={css({ _aboveSmallScreen: { marginRight: '60px' } })}
    />
  );
  const isCollapsible = markets.length > 4;
  const renderMarket = (market: Market, i: number) =>
    isCollapsible && !isExpanded && i >= 4 ? (
      <div key={market.name} className={css({ display: { base: 'contents', _smallScreen: 'none' } })}>
        <MarketPreview {...market} emitAnalyticsEvent={emitAnalyticsEvent} />
      </div>
    ) : (
      <MarketPreview {...market} key={market.name} emitAnalyticsEvent={emitAnalyticsEvent} />
    );
  const marketGrid = (
    <Box
      css={{
        display: 'grid',
        gap: { base: '800', _smallScreen: '200' },
        marginBottom: '800',
        // the filters act as a spacer, so need to add margin when the filters are not present
        marginTop: useFilters ? undefined : '600',
        gridAutoFlow: 'row',
        paddingRight: { _aboveSmallScreen: '1500' },
        gridTemplateColumns: {
          base: 'repeat(auto-fill, minmax(170px, 1fr))',
          _smallScreen: 'repeat(auto-fill, 160px)',
        },
      }}
    >
      {markets.map(renderMarket)}
    </Box>
  );
  return (
    <Card
      tone="neutral"
      css={{
        padding: '600',
        paddingInline: { _aboveSmallScreen: '600' },
        marginTop: '600',
      }}
    >
      <Heading level={2} textStyle="heading-sm" fontFamily="heading-alt">
        {heading}
      </Heading>
      {useFilters && (
        <Box
          css={{
            display: { base: 'flex', _smallScreen: 'contents' },
            flexDirection: 'row',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          {filterDisplay}
        </Box>
      )}

      {isCollapsible ? (
        <ShowHide.Root
          // oxlint-disable-next-line constellation/no-classname-css-on-components -- ShowHide.Root's props are ComponentProps<'div'>, so a css prop spreads onto the DOM instead of applying
          className={css({ _aboveSmallScreen: { display: 'contents' } })}
          isHidden={!isExpanded}
          onDisplayChange={() => setIsExpanded((prev) => !prev)}
          renderButton={(buttonProps) => (
            <ShowHide.Button
              aria-controls={buttonProps['aria-controls']}
              // oxlint-disable-next-line constellation/no-classname-css-on-components -- ShowHide.Root clones this with its own css prop, so only className survives
              className={css({ _aboveSmallScreen: { display: 'none' } })}
            >
              {isExpanded ? `See less ${subject}` : `See more ${subject}`}
            </ShowHide.Button>
          )}
        >
          {marketGrid}
        </ShowHide.Root>
      ) : (
        marketGrid
      )}
    </Card>
  );
};

export default MarketPreviews;
